import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState, lazy, Suspense } from 'react'
import Layout from './components/Layout/Layout'
import ShareLink from './components/ShareLink'
import { useStore } from './hooks/useStore'
import { loadFromCloud } from './utils/api'
import ScrollToTop from './components/Navigation/ScrollToTop'

// 路由代码拆分：仅在访问时加载对应页面的代码块
const Welcome = lazy(() => import('./pages/Welcome'))
const Calendar = lazy(() => import('./pages/past/Calendar'))
const LifeAreas = lazy(() => import('./pages/past/LifeAreas'))
const SixSentences = lazy(() => import('./pages/past/SixSentences'))
const SixQuestions = lazy(() => import('./pages/past/SixQuestions'))
const BestMoments = lazy(() => import('./pages/past/BestMoments'))
const AchievementsChallenges = lazy(() => import('./pages/past/AchievementsChallenges'))
const Forgiveness = lazy(() => import('./pages/past/Forgiveness'))
const Goodbye = lazy(() => import('./pages/past/Goodbye'))
const Dreams = lazy(() => import('./pages/future/Dreams'))
const Goals = lazy(() => import('./pages/future/Goals'))
const MagicThree = lazy(() => import('./pages/future/MagicThree'))
const NewYearSentences = lazy(() => import('./pages/future/NewYearSentences'))
const WordOfYear = lazy(() => import('./pages/future/WordOfYear'))
const SecretWish = lazy(() => import('./pages/future/SecretWish'))
const Complete = lazy(() => import('./pages/Complete'))

// 加载占位符
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
)

function App() {
  const { initStore, importData, _hasHydrated } = useStore()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function syncData(userId, force = false) {
      if (!userId) return;
      console.log(`[App] 检查云端数据更新 (ID: ${userId})...`)
      const cloudData = await loadFromCloud(userId)
      if (cloudData) {
        console.log('[App] 发现云端最新数据，正在应用...')
        importData(cloudData, force)
      } else {
        console.log('[App] 云端无数据')
      }
    }

    async function initialize() {
      // 只有在状态恢复（Hydrated）完成后才进行初始化逻辑
      // 否则本地旧数据可能在同步完成后才覆盖 Store
      if (!_hasHydrated) {
        console.log('[App] 等待持久化数据恢复...')
        return
      }

      try {
        const urlParams = new URLSearchParams(window.location.search)
        let urlId = urlParams.get('id')
        const localUserId = localStorage.getItem('yc-user-id')

        console.log(`[App] 初始化开始. URL ID: ${urlId}, 本地已存 ID: ${localUserId}`)

        if (!urlId) {
          // 如果 URL 没 ID，使用本地存的或生成新的
          const finalId = localUserId || `yc-${crypto.randomUUID().slice(0, 8)}`
          console.log(`[App] URL 无 ID，使用 ID: ${finalId}`)
          localStorage.setItem('yc-user-id', finalId)
          initStore(finalId)

          const newUrl = `${window.location.pathname}?id=${finalId}${window.location.hash}`
          window.history.replaceState({}, '', newUrl)

          // 尝试拉取该 ID 的云端备份
          await syncData(finalId)
        } else {
          // 如果 URL 有 ID，强制以 URL 为准
          console.log(`[App] 以 URL ID 为准: ${urlId}`)
          localStorage.setItem('yc-user-id', urlId)

          // 如果 URL ID 和本地当前 ID 不同，强制全量同步
          const currentStoreId = useStore.getState().userId
          const isDifferentUser = currentStoreId && currentStoreId !== urlId

          if (isDifferentUser) {
            console.log('[App] 检测到用户 ID 变更，清空本地状态并拉取新数据')
            useStore.getState().resetData(urlId)
          }

          initStore(urlId)
          await syncData(urlId, isDifferentUser)
        }
      } catch (error) {
        console.error('[App] 初始化失败:', error)
        initStore()
      } finally {
        setIsLoading(false)
        console.log('[App] 初始化完成')
      }
    }

    initialize()

    // 当用户切回标签页时，拉取云端更新
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && _hasHydrated) {
        const userId = localStorage.getItem('yc-user-id')
        if (userId) syncData(userId)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [initStore, importData, _hasHydrated])

  // 全局初始化加载状态
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">正在为你准备航向...</p>
        </div>
      </div>
    )
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      {/* 分享链接按钮 - 全局显示 */}
      <ShareLink />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Welcome />} />

            {/* 回顾 2025 */}
            <Route path="past/calendar" element={<Calendar />} />
            <Route path="past/life-areas" element={<LifeAreas />} />
            <Route path="past/six-sentences" element={<SixSentences />} />
            <Route path="past/six-questions" element={<SixQuestions />} />
            <Route path="past/best-moments" element={<BestMoments />} />
            <Route path="past/achievements" element={<AchievementsChallenges />} />
            <Route path="past/forgiveness" element={<Forgiveness />} />
            <Route path="past/goodbye" element={<Goodbye />} />

            {/* 规划 2026 */}
            <Route path="future/dreams" element={<Dreams />} />
            <Route path="future/goals" element={<Goals />} />
            <Route path="future/magic-three" element={<MagicThree />} />
            <Route path="future/new-year-sentences" element={<NewYearSentences />} />
            <Route path="future/word-of-year" element={<WordOfYear />} />
            <Route path="future/secret-wish" element={<SecretWish />} />

            {/* 完成 */}
            <Route path="complete" element={<Complete />} />

            {/* 404 重定向 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
